import { DateTime } from "luxon";
import type { DateRangeValue } from "./types";
import {
  PERIOD_LABELS,
  resolvePeriod,
  type PeriodId,
} from "../../logic/period";

const iso = (dt: DateTime): string => dt.toISODate() ?? "";

export type SingleShortcut = {
  id: string;
  label: string;
  compute: (now?: DateTime) => string;
};

export type RangeShortcut = {
  id: string;
  label: string;
  /** When set, the shortcut corresponds to a magic period (e.g. "thisMonth"). */
  period?: PeriodId;
  compute: (now?: DateTime) => DateRangeValue;
};

export const SINGLE_SHORTCUTS: SingleShortcut[] = [
  {
    id: "today",
    label: "Today",
    compute: (n = DateTime.now()) => iso(n),
  },
  {
    id: "yesterday",
    label: "Yesterday",
    compute: (n = DateTime.now()) => iso(n.minus({ days: 1 })),
  },
  {
    id: "tomorrow",
    label: "Tomorrow",
    compute: (n = DateTime.now()) => iso(n.plus({ days: 1 })),
  },
];

const periodShortcut = (period: PeriodId): RangeShortcut => ({
  id: period,
  label: PERIOD_LABELS[period],
  period,
  compute: (n = DateTime.now()) => {
    const r = resolvePeriod(period, n);
    return { from: r.startDate, to: r.endDate, period };
  },
});

export const RANGE_SHORTCUTS: RangeShortcut[] = [
  periodShortcut("today"),
  periodShortcut("yesterday"),
  periodShortcut("thisWeek"),
  periodShortcut("lastWeek"),
  periodShortcut("last7Days"),
  periodShortcut("thisMonth"),
  periodShortcut("lastMonth"),
  periodShortcut("last30Days"),
  periodShortcut("ytd"),
  periodShortcut("thisYear"),
  periodShortcut("lastYear"),
  {
    id: "all-time",
    label: "All time",
    compute: () => ({ from: null, to: null }),
  },
];

export const matchSingleShortcut = (
  v: string | null,
  now?: DateTime,
): SingleShortcut | undefined =>
  v === null ? undefined : SINGLE_SHORTCUTS.find((s) => s.compute(now) === v);

export const matchRangeShortcut = (
  v: DateRangeValue,
  now?: DateTime,
): RangeShortcut | undefined => {
  if (v.period) {
    return RANGE_SHORTCUTS.find((s) => s.period === v.period);
  }
  return RANGE_SHORTCUTS.find((s) => {
    const r = s.compute(now);
    return r.from === v.from && r.to === v.to && !s.period;
  });
};

export const formatSingle = (v: string | null, placeholder: string): string => {
  if (!v) return placeholder;
  const dt = DateTime.fromISO(v);
  return dt.isValid ? dt.toFormat("MMM d, yyyy") : placeholder;
};

export const formatRange = (v: DateRangeValue, now?: DateTime): string => {
  if (v.period) return PERIOD_LABELS[v.period];
  if (!v.from && !v.to) return "Any date";
  const shortcut = matchRangeShortcut(v, now);
  if (shortcut && shortcut.id !== "all-time") return shortcut.label;

  const fromDt = v.from ? DateTime.fromISO(v.from) : null;
  const toDt = v.to ? DateTime.fromISO(v.to) : null;

  if (fromDt && toDt) {
    if (fromDt.year === toDt.year) {
      return `${fromDt.toFormat("MMM d")} – ${toDt.toFormat("MMM d, yyyy")}`;
    }
    return `${fromDt.toFormat("MMM d, yyyy")} – ${toDt.toFormat("MMM d, yyyy")}`;
  }
  if (fromDt) return `From ${fromDt.toFormat("MMM d, yyyy")}`;
  if (toDt) return `Until ${toDt.toFormat("MMM d, yyyy")}`;
  return "Any date";
};
